import { Check, Edit } from '@mui/icons-material';
import { Alert, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { getProjectConfig } from '../../../modules/executions';
import { cloneTestCycle, completeTestCycle } from '../../../modules/testcycles';
import {
} from '../../../static-data/DropdownOptions';
import Modal from '../../elements/Modal';
import PlatformSelection from '../../elements/common/execution/PlatformSelectionForm';
import { checkRequiredFieldsInCycle } from '../../../lib/executionUtils';
import useExecutionScope from '../../../hooks/useExecutionScope';
import { useTestCycleConfig } from '../../../query/apiQueries';
import ConfirmationModal from '../../elements/ConfirmationModal';

const defaultValues = {
  type: null,
  purpose: null,
  platforms: [],
  subPlatforms: [],
  browsers: [],
  geographies: [],
};
const CloneTestCycleModal = ({
  show,
  handleClose,
  entityToClone,
  fetchTestCycles,
}) => {
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const { isPlatformsDefined } = useExecutionScope(projectId);
  const [projectConfig, setProjectConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [message, setMessage] = useState({ type: null, body: '' });
  const [currentStep, setCurrentStep] = useState(1);
  const [inputState, setInputState] = useState(defaultValues);

  const { data: config = [] } = useTestCycleConfig(projectId);

  const handleStepChange = (step) => {
    setCurrentStep(step);
    setMessage({ type: null, body: '' });
  };
  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);
      try {
        const response = await getProjectConfig(projectId);
        setProjectConfig(response);
      } catch (error) {
        console.error('Error fetching project config:', error);
      }
      setLoading(false);
    };

    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    if (show && entityToClone && config) {
      const isIncomplete = !entityToClone.completion_date;
      setCurrentStep(2);
      if (isIncomplete) {
        setMessage({
          type: 'warning',
          body: `The selected test cycle is incomplete. You can't clone it with the 
                 same type, purpose, or platforms. Complete the cycle or choose a 
                 different configuration.`,
        });
      }
      setInputState({
        type: entityToClone?.type_details?.id || null,
        purpose: entityToClone?.purpose_details?.id || null,
        platforms: entityToClone?.platforms || [],
        subPlatforms: entityToClone?.sub_platforms || [],
        browsers: entityToClone?.browsers || [],
        geographies: entityToClone?.geographies || [],
      });
    }
  }, [show, entityToClone, config]);

  const handleChange = (field, value) => {
    setInputState((prev) => ({ ...prev, [field]: value }));
    setMessage({ type: null, body: '' });
  };

  const handleCloneTestcycle = async (force = false) => {
    if (isPlatformsDefined) {
      const errorMessage = checkRequiredFieldsInCycle(inputState);
      if (errorMessage) {
        setMessage({ type: 'error', body: errorMessage });
        if (currentStep === 1) setCurrentStep(3);
        return;
      }
    }
    const clonedCycle = {
      type: inputState.type || entityToClone.typeId,
      purpose: inputState.purpose || entityToClone.purposeId,
      platforms: inputState.platforms || entityToClone.platforms,
      subPlatforms: inputState.subPlatforms || entityToClone.sub_platforms,
      browsers: inputState.browsers || entityToClone.browsers,
      geographies: inputState.geographies || entityToClone.geographies,
      force,
    };

    setLoading(true);
    setMessage({ type: 'info', body: 'Cloning test cycle. Please wait...' });

    try {
      const response = await cloneTestCycle(projectId, {
        testCycleId: entityToClone.id,
        testcycle: clonedCycle,
      });
      if (response?.is_duplicate) {
        setMessage({ type: null, body: '' });
        setDuplicateMessage(response.message);
        setShowDuplicateConfirm(true);
        setLoading(false);
        return;
      }
      if (!response?.success) {
        setMessage({ type: 'error', body: response?.message });
        setCurrentStep(3);
        setLoading(false);
        return;
      }
      fetchTestCycles();
      addNotification('success', 'Test cycle cloned successfully!');
      handleCloseModal();
    } catch (error) {
      setMessage({
        type: 'error',
        body: error.message || 'Failed to clone test cycle.',
      });
    }
    setLoading(false);
  };

  const handleCompleteTestCycle = async () => {
    setLoading(true);
    setMessage({ type: 'info', body: 'Completing test cycle. Please wait...' });
    try {
      await completeTestCycle(entityToClone?.id);
      setMessage({
        type: 'success',
        body: 'Previous test cycle has been marked as completed!',
      });
      setCurrentStep(1);
    } catch (error) {
      setMessage({ type: 'error', body: 'Failed to complete the test cycle.' });
    }
    setLoading(false);
  };

  const handleCloseModal = () => {
    handleStepChange(1);
    setInputState(defaultValues);
    handleClose();
  };

  return (
    <>
      <Modal
        open={show}
        handleClose={handleCloseModal}
        modalTitle="Clone Test Cycle"
        action={() => (
          <>
          <Button
            variant="outline-secondary"
            onClick={handleCloseModal}
            title="Cancel">
              Cancel
            </Button>

            {currentStep === 1 || currentStep === 3
            ? !loading &&
              message.type !== 'error' && (
                  <Button
                    variant="primary"
                    onClick={() => handleCloneTestcycle()}
                    disabled={currentStep === 3 && !inputState.type}>
                    Clone
                  </Button>
                )
              : null}
          </>
        )}>
        <>
          {message?.type && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.body}
            </Alert>
          )}
          {loading ? null : currentStep === 2 ? (
          <>
            <div className="d-flex justify-content-evenly align-items-center mt-3 gap-2">
              <Button variant="success" onClick={!entityToClone?.completion_date ? handleCompleteTestCycle : handleCloneTestcycle}>
                <Check sx={{ color: 'white' }} />
                <span style={{ marginLeft: '5px' }}>
                  {!entityToClone?.completion_date ? 'Complete Selected Cycle and Clone' : 'Clone Selected Cycle'}
                </span>
              </Button>
              <Button variant="primary" onClick={() => handleStepChange(3)}>
                <Edit sx={{ color: 'white' }} />
                <span style={{ marginLeft: '5px' }}>
                  Change Configuration of New Cycle
                </span>
              </Button>
            </div>
          </>
          ) : currentStep === 3 ? (
            <>
              <Row>
                <Typography variant="h6" className="mb-2">
                  Select Configurations
                </Typography>
                <Col sm={4}>
                  <Form.Group controlId="type">
                    <Form.Label>Test Cycle Type</Form.Label>
                    <Form.Select
                    as="select"
                      value={inputState.type || ''}
                      onChange={(e) => handleChange('type', e.target.value)}
                      className="form-input">
                    <option value="" disabled>
                      None
                    </option>
                      {config?.type?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group controlId="purpose">
                    <Form.Label>Purpose</Form.Label>
                    <Form.Select
                    as="select"
                      value={inputState.purpose || ''}
                      onChange={(e) => handleChange('purpose', e.target.value)}
                      className="form-input">
                    <option value="" disabled>
                      None
                    </option>
                      {config?.purpose?.map((purpose) => (
                      <option key={purpose.id} value={purpose.id}>
                        {purpose.name}
                      </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-2">
                <PlatformSelection
                  platforms={inputState.platforms}
                  subPlatforms={inputState.subPlatforms}
                  projectConfig={projectConfig}
                  setFieldValue={handleChange}
                  isEditing={true}
                  browsers={inputState.browsers}
                  geographies={inputState.geographies}
                />
              </Row>
            </>
          ) : null}
        </>
      </Modal>
      <ConfirmationModal
        show={showDuplicateConfirm}
        handleClose={() => setShowDuplicateConfirm(false)}
        handleConfirm={() => { setShowDuplicateConfirm(false); handleCloneTestcycle(true); }}
        title="Duplicate Test Cycle"
        body={
          <>
            {duplicateMessage}
            <br />
            <strong>Do you want to clone it anyway?</strong>
          </>
        }
        buttonLabel="Continue Anyway"
        buttonLabelSecondary="Cancel"
        buttonStyle="primary"
      />
    </>
  );
};

export default CloneTestCycleModal;
